import { create_pwa_pin, fetch_pwa_pin, type PwaPinPayload, type PwaPinState } from './api'
import { canonical_client_search, is_client_flow_path, parse_cnpj_from_search } from './client-paths'
import { browser_session_storage } from './browser-storage'
import { DRIVER_TYPE_APP } from './driver-state'
import { is_driver_flow_path } from './driver-paths'
import { is_native_app } from './runtime'
import { is_samsung_internet_webapk_blocked } from './app-install'
import { store, type AppState } from './store'
import { is_rede_mode, rede_display_name } from './rede-context'
import {
  APP_BRAND_FAVICON_96_SRC,
  APP_BRAND_MARK_SRC,
  APP_BRAND_NAME,
  APP_BRAND_TOUCH_ICON_SRC,
} from './brand'

const MANIFEST_LINK_ID = 'app-manifest'
const PIN_QUERY_KEY = 'pin'
const OFFERS_SHORTCUT_TITLE = 'Na Bomba'
const PIN_STATE_KEYS = [
  'cnpj',
  'phone',
  'auth_token',
  'station_name',
  'station_location',
  'station_logo',
  'latitude',
  'longitude',
  'whatsapp_link',
  'voucher',
  'driver_phone',
  'driver_tipo_motorista',
  'driver_placa',
  'driver_autorizacao',
  'driver_veiculo',
  'driver_offer_product_code',
  'driver_offer_payment_id',
  'driver_voucher',
] as const satisfies readonly (keyof AppState)[]
const DRIVER_PIN_STATE_KEYS = PIN_STATE_KEYS.filter(key => key.startsWith('driver_')) as readonly PinStateKey[]
const DRIVER_APP_RESTORE_KEYS = [
  'driver_tipo_motorista',
  'driver_placa',
  'driver_autorizacao',
  'driver_veiculo',
] as const satisfies readonly PinStateKey[]

type PinStateKey = (typeof PIN_STATE_KEYS)[number]
type ShortcutManifestContext = { token: string; manifest_url: string }
type RouteTarget = { path: string; search: string; hash: string }
type DriverRestoreState = Pick<AppState, (typeof DRIVER_APP_RESTORE_KEYS)[number]>

let refresh_timer: number | null = null
let refresh_seq = 0
let last_payload_json = ''
let last_manifest_href = ''
let last_manifest_token = ''
let shortcut_init_done = false
let manual_install_restore_url: string | null = null
const MANUAL_INSTALL_HELP_KEY = 'fidelidade_install_help_pending'
const document_loaded_with_pin = new URLSearchParams(window.location.search).has(PIN_QUERY_KEY)

function clean_cnpj(value: string): string | null {
  const digits = (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').replace(/\D/g, '')
  return digits.length === 14 ? digits : null
}

function text(value: unknown): string {
  return (typeof value === 'string' ? value : typeof value === 'number' || typeof value === 'boolean' || typeof value === 'bigint' ? String(value) : '').trim()
}

function has_driver_profile_state(values: Partial<DriverRestoreState>): boolean {
  return Boolean(values.driver_placa || values.driver_autorizacao || values.driver_veiculo)
}

function is_driver_app_restore_key(key: PinStateKey): key is (typeof DRIVER_APP_RESTORE_KEYS)[number] {
  return (DRIVER_APP_RESTORE_KEYS as readonly PinStateKey[]).includes(key)
}

function preserved_driver_app_state(route_path: string, state: PwaPinState): DriverRestoreState | null {
  if (!is_driver_flow_path(route_path)) return null

  const incoming_driver_phone = text(state.driver_phone)
  const stored_driver_phone = text(store.get('driver_phone'))
  if (!incoming_driver_phone || incoming_driver_phone !== stored_driver_phone) return null

  const stored_state: DriverRestoreState = {
    driver_tipo_motorista: store.get('driver_tipo_motorista'),
    driver_placa: store.get('driver_placa'),
    driver_autorizacao: store.get('driver_autorizacao'),
    driver_veiculo: store.get('driver_veiculo'),
  }
  if (Number(stored_state.driver_tipo_motorista) !== DRIVER_TYPE_APP || !has_driver_profile_state(stored_state)) {
    return null
  }

  const incoming_is_driver_app = Number(state.driver_tipo_motorista) === DRIVER_TYPE_APP
  if (incoming_is_driver_app && has_driver_profile_state(state as Partial<DriverRestoreState>)) {
    return null
  }

  return stored_state
}

function preserved_driver_auth_token(route_path: string, state: PwaPinState): string | null {
  if (!is_driver_flow_path(route_path)) return null
  if (Object.prototype.hasOwnProperty.call(state, 'auth_token')) return null

  const incoming_driver_phone = text(state.driver_phone)
  const stored_driver_phone = text(store.get('driver_phone'))
  const stored_auth_token = text(store.get('auth_token'))
  if (!incoming_driver_phone || incoming_driver_phone !== stored_driver_phone || !stored_auth_token) return null
  return stored_auth_token
}

function preserved_client_auth_token(route_path: string, state: PwaPinState): string | null {
  if (!is_client_flow_path(route_path)) return null
  if (Object.prototype.hasOwnProperty.call(state, 'auth_token')) return null

  const incoming_phone = text(state.phone)
  const incoming_cnpj = clean_cnpj(text(state.cnpj))
  const stored_phone = text(store.get('phone'))
  const stored_cnpj = clean_cnpj(text(store.get('cnpj')))
  const stored_auth_token = text(store.get('auth_token'))
  if (!incoming_phone || !incoming_cnpj || !stored_auth_token) return null
  if (incoming_phone !== stored_phone || incoming_cnpj !== stored_cnpj) return null
  return stored_auth_token
}

function is_standalone(): boolean {
  return is_native_app()
    || window.matchMedia('(display-mode: standalone)').matches
    || (navigator as Navigator & { standalone?: boolean }).standalone === true
}

function is_browser_pwa_install_blocked(): boolean {
  return is_samsung_internet_webapk_blocked(navigator.userAgent || '')
}

function ensure_manifest_link(): HTMLLinkElement {
  let link = document.getElementById(MANIFEST_LINK_ID) as HTMLLinkElement | null
  if (link) return link

  link = document.createElement('link')
  link.id = MANIFEST_LINK_ID
  link.rel = 'manifest'
  document.head.appendChild(link)
  return link
}

function set_manifest_href(href: string): void {
  if (is_browser_pwa_install_blocked()) {
    document.getElementById(MANIFEST_LINK_ID)?.remove()
    last_manifest_href = ''
    return
  }
  const link = ensure_manifest_link()
  if (last_manifest_href === href && link.getAttribute('href') === href) return

  const next = link.cloneNode(false) as HTMLLinkElement
  next.id = MANIFEST_LINK_ID
  next.rel = 'manifest'
  next.href = href
  link.replaceWith(next)
  last_manifest_href = href
}

function clean_search(raw_search: string): string {
  const raw = raw_search && raw_search !== '?'
    ? (raw_search.startsWith('?') ? raw_search : `?${raw_search}`)
    : ''
  if (!raw) return ''
  const canonical = canonical_client_search(raw)
  const params = new URLSearchParams(canonical)
  let changed = canonical !== raw
  if (params.has(PIN_QUERY_KEY)) {
    params.delete(PIN_QUERY_KEY)
    changed = true
  }
  const returnPath = params.get('voltar')
  if (returnPath) {
    const cleanReturnPath = route_without_pin(returnPath)
    if (cleanReturnPath !== returnPath) {
      params.set('voltar', cleanReturnPath)
      changed = true
    }
  }
  if (!changed) return canonical
  const next = params.toString()
  return next ? `?${next}` : ''
}

function clean_current_search(): string {
  return clean_search(window.location.search)
}

function current_location_path(): string {
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || '/'
}

function route_without_pin(raw_path: string): string {
  const raw = String(raw_path || '')
  const hash_index = raw.indexOf('#')
  const hash = hash_index === -1 ? '' : raw.slice(hash_index)
  const without_hash = hash_index === -1 ? raw : raw.slice(0, hash_index)
  const query_index = without_hash.indexOf('?')
  if (query_index === -1) return raw

  const path = without_hash.slice(0, query_index) || '/'
  const params = new URLSearchParams(without_hash.slice(query_index))
  if (!params.has(PIN_QUERY_KEY)) return raw
  params.delete(PIN_QUERY_KEY)
  const search = params.toString()
  return `${path}${search ? `?${search}` : ''}${hash}`
}

function is_offers_shortcut_path(path = window.location.pathname): boolean {
  return is_driver_flow_path(path)
}

function split_route_target(raw_target: string): RouteTarget {
  const target = String(raw_target || '').trim() || '/'
  const hash_index = target.indexOf('#')
  const hash = hash_index === -1 ? '' : target.slice(hash_index)
  const without_hash = hash_index === -1 ? target : target.slice(0, hash_index)
  const query_index = without_hash.indexOf('?')
  const path = query_index === -1 ? without_hash : without_hash.slice(0, query_index)
  const search = query_index === -1 ? '' : without_hash.slice(query_index)

  return {
    path: path.startsWith('/') ? (path || '/') : `/${path.replace(/^\/+/, '')}`,
    search: clean_search(search),
    hash,
  }
}

function sanitized_search_for_route(path: string, raw_search: string): string {
  const raw = clean_search(raw_search)
  if (!raw) return ''
  if (!is_offers_shortcut_path(path)) return raw

  const bare = raw.startsWith('?') ? raw.slice(1) : raw
  const bare_cnpj = bare && !bare.includes('=') ? clean_cnpj(bare) : null
  if (bare_cnpj) return ''

  const params = new URLSearchParams(raw)
  params.delete(PIN_QUERY_KEY)
  params.delete('cnpj')
  const next = params.toString()
  return next ? `?${next}` : ''
}

function search_with_pin_from(raw_search: string, token: string): string {
  const raw = clean_search(raw_search)
  const bare = raw.startsWith('?') ? raw.slice(1) : raw
  const params = new URLSearchParams()
  const bare_cnpj = bare && !bare.includes('=') ? clean_cnpj(bare) : null

  if (bare_cnpj) {
    params.set('cnpj', bare_cnpj)
  } else if (raw) {
    const current = new URLSearchParams(raw)
    current.delete(PIN_QUERY_KEY)
    current.forEach((value, key) => params.append(key, value))
  }

  params.set(PIN_QUERY_KEY, token)
  const next = params.toString()
  return next ? `?${next}` : ''
}

function search_with_pin(token: string): string {
  return search_with_pin_from(
    sanitized_search_for_route(window.location.pathname, window.location.search),
    token,
  )
}

function route_with_pin(target_path: string, token: string): string {
  const target = split_route_target(target_path)
  const search = sanitized_search_for_route(target.path, target.search)
  return `${target.path}${search_with_pin_from(search, token)}${target.hash}` || '/'
}

function route_with_current_origin_ticket(target_path: string): string {
  const ticket = new URLSearchParams(window.location.search).get('origin_ticket') || ''
  if (!/^[A-Za-z0-9_-]{20,80}$/.test(ticket)) return target_path

  const target = new URL(target_path, 'https://pwa-runtime.invalid')
  if (target.pathname !== window.location.pathname) return target_path
  target.searchParams.set('origin_ticket', ticket)
  return `${target.pathname}${target.search}${target.hash}` || '/'
}

function runtime_url_with_pin(token: string): string {
  return `${window.location.pathname}${search_with_pin(token)}${window.location.hash}` || '/'
}

function clean_runtime_url(): string {
  return `${window.location.pathname}${clean_current_search()}${window.location.hash}` || '/'
}

function sync_runtime_pin_url(token: string | null): void {
  const next = token
    ? runtime_url_with_pin(token)
    : clean_runtime_url()
  if (current_location_path() === next) return
  window.history.replaceState({}, '', next)
}

function station_icon_url(size: 96 | 180 | 192 | 512): string {
  if (is_rede_mode()) return `/api/pwa/icon.png?size=${size}`
  if (is_offers_shortcut_path()) {
    if (size === 96) return APP_BRAND_FAVICON_96_SRC
    if (size === 192) return APP_BRAND_MARK_SRC
    return APP_BRAND_TOUCH_ICON_SRC
  }
  if (!store.get('phone')) {
    if (size === 96) return APP_BRAND_FAVICON_96_SRC
    if (size === 192) return APP_BRAND_MARK_SRC
    return APP_BRAND_TOUCH_ICON_SRC
  }
  const params = new URLSearchParams({ size: String(size) })
  const cnpj = clean_cnpj(String(store.get('cnpj') || ''))
  if (last_manifest_token) params.set('pin', last_manifest_token)
  else if (cnpj) params.set('cnpj', cnpj)
  else return APP_BRAND_TOUCH_ICON_SRC
  return `/api/pwa/icon.png?${params.toString()}`
}

function station_install_title(): string {
  if (is_rede_mode()) return rede_display_name()
  if (is_offers_shortcut_path()) return OFFERS_SHORTCUT_TITLE
  const has_client_session = !!store.get('phone')
  const station_name = String(store.get('station_name') || '').trim()
  return has_client_session && station_name ? station_name : APP_BRAND_NAME
}

function ensure_apple_title_meta(): HTMLMetaElement {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
  if (meta) return meta
  meta = document.createElement('meta')
  meta.name = 'apple-mobile-web-app-title'
  document.head.appendChild(meta)
  return meta
}

function ensure_touch_icon_link(): HTMLLinkElement {
  let link = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  if (link) return link
  link = document.createElement('link')
  link.rel = 'apple-touch-icon'
  link.sizes = '180x180'
  document.head.appendChild(link)
  return link
}

function ensure_favicon_link(size: 96 | 192): HTMLLinkElement {
  const selector = `link[rel="icon"][sizes="${size}x${size}"]`
  let link = document.querySelector<HTMLLinkElement>(selector)
  if (link) return link
  link = document.createElement('link')
  link.rel = 'icon'
  link.type = 'image/png'
  link.sizes = `${size}x${size}`
  document.head.appendChild(link)
  return link
}

function sync_runtime_install_branding(): void {
  const title = station_install_title()
  document.title = title
  ensure_apple_title_meta().content = title
  ensure_touch_icon_link().href = station_icon_url(180)
  ensure_favicon_link(96).href = station_icon_url(96)
  ensure_favicon_link(192).href = station_icon_url(192)
}

function build_pin_state(): PwaPinState {
  const state: PwaPinState = {}
  for (const key of PIN_STATE_KEYS) {
    if (key === 'auth_token') continue
    const value = store.get(key)
    if (value === undefined || value === null) continue
    state[key] = value
  }
  return state
}

function build_pin_payload(): PwaPinPayload {
  return build_pin_payload_for_route(current_location_path())
}

function build_pin_payload_for_route(target_path: string): PwaPinPayload {
  const target = split_route_target(target_path)
  const state = build_pin_state()
  if (is_offers_shortcut_path(target.path)) {
    for (const key of PIN_STATE_KEYS) {
      if (DRIVER_PIN_STATE_KEYS.includes(key) || key === 'auth_token') continue
      delete state[key]
    }
  }
  return {
    v: 3,
    route_path: target.path,
    route_search: sanitized_search_for_route(target.path, target.search),
    state,
  }
}

function payload_cache_json(payload: PwaPinPayload): string {
  return JSON.stringify({
    v: payload.v,
    route_path: payload.route_path || '/',
    route_search: payload.route_search || '',
    state: payload.state || {},
  })
}

function sync_manifest_context(ctx: ShortcutManifestContext, payload: PwaPinPayload): void {
  set_manifest_href(ctx.manifest_url)
  last_payload_json = payload_cache_json(payload)
  last_manifest_token = ctx.token
}

function apply_pin_state(state: PwaPinState, route_path: string): void {
  const preserved_driver_state = preserved_driver_app_state(route_path, state)
  const preserved_auth_token = preserved_driver_auth_token(route_path, state)
    || preserved_client_auth_token(route_path, state)
  for (const key of PIN_STATE_KEYS) {
    if (preserved_driver_state && is_driver_app_restore_key(key)) {
      store.set(key, preserved_driver_state[key])
      continue
    }
    if (key === 'auth_token' && preserved_auth_token) {
      store.set(key, preserved_auth_token)
      continue
    }
    const next = (state as Record<string, unknown>)[key] ?? null
    store.set(key, next as AppState[PinStateKey])
  }
}

async function refresh_manifest(force = false): Promise<ShortcutManifestContext | null> {
  const seq = ++refresh_seq
  const payload = build_pin_payload()
  const payload_json = payload_cache_json(payload)
  if (!force && payload_json === last_payload_json && last_manifest_href && last_manifest_token) {
    sync_runtime_pin_url(last_manifest_token)
    return { token: last_manifest_token, manifest_url: last_manifest_href }
  }

  try {
    const ctx = await create_pwa_pin(payload)
    // Uma resposta da tela anterior não pode trocar o PIN da etapa atual.
    if (seq !== refresh_seq || payload_json !== payload_cache_json(build_pin_payload())) return null
    sync_manifest_context(ctx, payload)
    sync_runtime_pin_url(ctx.token)
    return ctx
  } catch (err) {
    console.warn('[pwa] falha ao atualizar manifest:', err)
    return null
  }
}

export function init_pwa_shortcut(): void {
  if (shortcut_init_done) return
  shortcut_init_done = true

  store.subscribe(() => {
    sync_runtime_install_branding()
    schedule_pwa_shortcut_refresh()
  })
  window.addEventListener('popstate', () => schedule_pwa_shortcut_refresh())
  if (is_browser_pwa_install_blocked()) {
    document.getElementById(MANIFEST_LINK_ID)?.remove()
  }
  sync_runtime_install_branding()
  schedule_pwa_shortcut_refresh()
}

export function schedule_pwa_shortcut_refresh(): void {
  if (refresh_timer !== null) window.clearTimeout(refresh_timer)
  refresh_timer = window.setTimeout(() => {
    refresh_timer = null
    void refresh_manifest()
  }, 180)
}

export async function ensure_pwa_shortcut_manifest(): Promise<ShortcutManifestContext | null> {
  return refresh_manifest(true)
}

export async function prepare_pwa_shortcut_path(target_path: string): Promise<string> {
  // A navegação prepara seu próprio PIN. Cancele a atualização da rota de
  // origem e invalide respostas em voo antes de carregar a próxima etapa.
  if (refresh_timer !== null) {
    window.clearTimeout(refresh_timer)
    refresh_timer = null
  }
  const seq = ++refresh_seq
  const payload = build_pin_payload_for_route(target_path)
  const payload_json = payload_cache_json(payload)
  if (payload_json === last_payload_json && last_manifest_href && last_manifest_token) {
    return route_with_pin(target_path, last_manifest_token)
  }

  const ctx = await create_pwa_pin(payload)
  if (seq === refresh_seq) sync_manifest_context(ctx, payload)
  return route_with_pin(target_path, ctx.token)
}

export async function prepare_pwa_shortcut_install(options: { reload_document?: boolean } = {}): Promise<'ready' | 'reloading'> {
  const current = current_location_path()
  const next = await prepare_pwa_shortcut_path(current)
  if (!manual_install_restore_url) manual_install_restore_url = current
  const should_reload_document = !!options.reload_document && !document_loaded_with_pin
  if (should_reload_document) {
    browser_session_storage.setItem(MANUAL_INSTALL_HELP_KEY, '1')
    if (current === next) window.location.reload()
    else window.location.replace(next)
    return 'reloading'
  }
  if (current === next) return 'ready'
  window.history.replaceState({}, '', next)
  return 'ready'
}

export function restore_pwa_shortcut_install_url(): void {
  if (!manual_install_restore_url) return
  const next = manual_install_restore_url
  manual_install_restore_url = null
  if (current_location_path() === next) return
  window.history.replaceState({}, '', next)
}

function valid_runtime_pin_token(value: unknown): string | null {
  const token = text(value)
  return /^[A-Za-z0-9_.-]{32,4096}$/.test(token) ? token : null
}

async function renew_unreadable_pin(): Promise<string | null> {
  const clean_target = clean_runtime_url()
  try {
    const payload = build_pin_payload_for_route(clean_target)
    const ctx = await create_pwa_pin(payload)
    sync_manifest_context(ctx, payload)
    const next = route_with_pin(clean_target, ctx.token)
    if (current_location_path() !== next) window.history.replaceState({}, '', next)
    return next
  } catch (err) {
    // A falha do PIN nunca impede a rota atual de abrir. Remover apenas o token
    // ilegível evita repetir a mesma restauração em loop nesta execução.
    sync_runtime_pin_url(null)
    console.warn('[pwa] pin ilegível ignorado; renovação falhou:', err)
    return null
  }
}

export async function restore_pwa_shortcut(): Promise<string | null> {
  const token = new URLSearchParams(window.location.search).get(PIN_QUERY_KEY)
  if (!token) return null

  try {
    const payload = await fetch_pwa_pin(token)
    if (!payload) return renew_unreadable_pin()
    const explicit_cnpj = parse_cnpj_from_search(window.location.search)
    const pinned_cnpj = clean_cnpj(text(payload.state?.cnpj))
      || parse_cnpj_from_search(payload.route_search || '')
    if (explicit_cnpj && pinned_cnpj !== explicit_cnpj) {
      console.warn('[pwa] pin de outro posto ignorado; o CNPJ explícito do QR Code prevalece')
      sync_runtime_pin_url(null)
      return null
    }
    apply_pin_state(payload.state || {}, payload.route_path || '/')
    const replacement_token = valid_runtime_pin_token(payload.replacement_token)
    const active_token = replacement_token || token
    const active_payload = replacement_token ? { ...payload, v: 3 as const } : payload
    sync_manifest_context(
      { token: active_token, manifest_url: `/api/pwa/manifest.json?pin=${encodeURIComponent(active_token)}` },
      active_payload,
    )
    // O ticket nasce depois do pin. Preservá-lo aqui evita apagar o handoff de
    // uso único e repetir indefinidamente authorize -> validar-origem.
    const pinned_route = route_with_pin(`${payload.route_path || '/'}${payload.route_search || ''}`, active_token)
    const next = route_with_current_origin_ticket(pinned_route)
    if (current_location_path() !== next) window.history.replaceState({}, '', next)
    return next
  } catch (err) {
    console.warn('[pwa] falha ao restaurar atalho:', err)
    return null
  }
}

export function consume_pending_pwa_shortcut_install_help(): boolean {
  const pending = browser_session_storage.getItem(MANUAL_INSTALL_HELP_KEY) === '1'
  if (pending) browser_session_storage.removeItem(MANUAL_INSTALL_HELP_KEY)
  return pending
}

export { is_standalone as is_pwa_standalone }
